import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOMServer from 'react-dom/server';
import { HelmetProvider } from 'react-helmet-async';

import { auth, db } from "../../../firebase";
import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile, signOut } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

import SEO from '../../SEO';
import Popup from "../../mobile/Popup";
import PrivacyPolicy from "../../PrivacyPolicy"; // PrivacyPolicy 컴포넌트 임포트

import styleClasses from '../css/mobile/LoginSignUp.module.scss';


function Signup() {
  const navigate      = useNavigate();

  const emailRef      = useRef();
  const passwordRef   = useRef();
  const nameRef       = useRef();
  const isSubmitting  = useRef(false);

  const [email, setEmail]                         = useState("");
  const [emailError, setEmailError]               = useState('');
  const [emailErrorText, setEmailErrorText]       = useState('');
  const [password, setPassword]                   = useState("");
  const [passwordError, setPasswordError]         = useState('');
  const [passwordErrorText, setPasswordErrorText] = useState('');
  const [name, setName]                           = useState("");
  const [buttonLoading, setButtonLoading]         = useState(false);
  const [nameError, setNameError]                 = useState('');
  const [nameErrorText, setNameErrorText]         = useState('');
  const [agree, setAgree]                         = useState(false);
  const [error, setError]                         = useState("");
  const [openPopup, setOpenPopup]                 = useState(false);
  const [popupTitle, setPopupTitle]               = useState('');
  const [popupText, setPopupText]                 = useState('');
  const [popupFunction, setPopupFunction]         = useState('');
  const [popupButtonText, setPopupButtonText]     = useState('');

  // 이메일 유효성 검사
  const validateEmail = (email) => {
    if (email === '') {
      setEmailErrorText('이메일 주소를 입력하세요.');
      return false;
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (emailRegex.test(email) === false) {
        setEmailErrorText('올바른 이메일 주소를 입력하세요.');
      }
      return emailRegex.test(email);
    }
  };

  // 🔹 비밀번호 유효성 검사 함수
  const validatePassword = (password) => {
    if (password === '') {
      setPasswordErrorText("비밀번호를 입력하세요.");
      return false;
    } else {
      const minLength = password.length >= 8;
      const hasNumber = /\d/.test(password); // 숫자 포함 여부
      const hasSpecialChar = /[!@#$%^&*]/.test(password); // 특수문자 포함 여부
      if ((minLength && hasNumber && hasSpecialChar) === false) {
        setPasswordErrorText("비밀번호는 최소 8자 이상이며, 숫자와 특수문자(!@#$%^&*)를 반드시 포함해야 합니다.");
      }
      return minLength && hasNumber && hasSpecialChar;
    }
  };

  const resetError = (event) => {
    if (event.target.id === 'email') {
      setEmailError('');
      setEmailErrorText('');
    } else if (event.target.id === 'password') {
      setPasswordError('');
      setPasswordErrorText('');
    } else if (event.target.id === 'name') {
      setNameError('');
      setNameErrorText('');
    }
    setError('');
  };

  const handleSignup = async () => {
   if (!validateEmail(email)) {
      setEmailError('error');
      emailRef.current.focus();
      return;
    } else if (!validatePassword(password)) {
      setPasswordError('error');
      passwordRef.current.focus();
      // setError("비밀번호는 최소 8자 이상이며, 숫자 및 특수문자(!@#$%^&*)를 포함해야 합니다.");
      return;
    } else if (name === '') {
      setNameError('error');
      setNameErrorText("이름을 입력하세요.");
      nameRef.current.focus();
      return;
    } else if (!agree) {
      privacyAlertPopup();
      return;
    }

    try {
      if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
      isSubmitting.current = true;      // 버튼 비활성화
      setButtonLoading(true);           // 버튼 로딩 표시

      // 🔹 Firebase Authentication - 사용자 계정 생성
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 🔹 Firebase Auth 프로필 업데이트: displayName 설정
      await updateProfile(user, {
        displayName: name,
      });

      // 🔹 Firestore - 유저 정보 저장 (pending 상태)
      await setDoc(
        doc(db, "users", user.uid),
        {
          email,
          name,
          agree,
          cardslot: 1,
          type: "email",  // 로그인 방식 구분 (이메일 가입)
          status: "pending", // 이메일 인증 전에는 pending 상태
          createdAt: new Date().toISOString(),
          musicard: {}     // 빈 객체로 초기화
        },
        { merge: true }
      );

      // 추가로 users_data 컬렉션에 동일 UID의 문서를 생성 (필요한 초기 데이터 구성)
      await setDoc(doc(db, "users_data", user.uid), {
        createdAt: new Date().toISOString(),
      });

      // 추가로 users_logs 컬렉션에 동일 UID의 문서를 생성 (필요한 초기 데이터 구성)
      await setDoc(doc(db, "users_logs", user.uid), {
        createdAt: new Date().toISOString(),
      });

      // // 🔹 이메일 인증 메일 전송
      await sendEmailVerification(user);
      await signOut(auth); // 회원가입 후 자동 로그아웃

      setEmail('');
      setPassword('');
      setName('');
      setAgree(false);
      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김
      // alert("회원가입이 완료되었습니다. 이메일 인증 후 로그인 가능합니다.");
      setOpenPopup(true);
      setPopupTitle('회원가입이 완료되었습니다. 이메일 인증 후 로그인 가능합니다.');
      setPopupText('');
      setPopupButtonText('메인으로 이동');
      setPopupFunction(() => () => navigate('/'));
    } catch (err) {
      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김
      setError(err.message);
    }
  };

  const privacyPolicyHTML = ReactDOMServer.renderToStaticMarkup(
    <HelmetProvider>
      <PrivacyPolicy />
    </HelmetProvider>
  );
  
  // 팝업
  // 팝업 - 개인정보 수집·이용 동의서
  const privacyPopup = () => {
    setPopupTitle('개인정보 수집·이용 동의서');
    setPopupText(privacyPolicyHTML);
    setPopupFunction(() => agreeOnPopup);
    setPopupButtonText('동의');
    setOpenPopup(true);
  }
  // 팝업 - 개인정보 수집·이용 동의서 (Alert)
  const privacyAlertPopup = () => {
    setPopupTitle('개인정보 수집·이용에 동의해야 합니다.');
    setPopupText(privacyPolicyHTML);
    setPopupFunction(() => agreeOnPopup);
    setPopupButtonText('동의');
    setOpenPopup(true);
  }
  // 팝업 - 함수 (클릭 시 개인정보 수집·이용 동의서 체크)
  const agreeOnPopup = () => {
    setAgree(true);
    setOpenPopup(false);
  };

  return (
    <>
      <SEO title='회원가입' />
      <div className={ styleClasses.signUpMobile }>
        <div className={ styleClasses.inner }>
          <div className={ styleClasses.title }>
            <h1>회원가입</h1>
          </div>
          <div className="commonForm">
            <div className='formRow fullWidth'>
              <div className="labelBox">
                <label htmlFor="email">ID (이메일)</label>
              </div>
              <div className={`fieldBox fullWidth ${ emailError }`}>
                <input type="email" id='email' name='email' placeholder="이메일" value={email} onChange={(e) => { setEmail(e.target.value); resetError(e); }} ref={ emailRef } />
                <div className="fieldError">
                  <p>{ emailErrorText }</p>
                </div>
              </div>
            </div>
            <div className='formRow fullWidth'>
              <div className="labelBox">
                <label htmlFor="password">비밀번호</label>
              </div>
              <div className={`fieldBox fullWidth ${ passwordError }`}>
                <input type="password" id='password' name='password' placeholder="비밀번호 (최소 8자, 숫자 & 특수문자 포함)" value={password} onChange={(e) => { setPassword(e.target.value);  resetError(e); }}  ref={ passwordRef } />
                <div className="fieldError">
                  <p>{ passwordErrorText }</p>
                </div>
              </div>
            </div>
            <div className='formRow fullWidth'>
              <div className="labelBox">
                <label htmlFor="name">이름</label>
              </div>
              <div className={`fieldBox fullWidth ${ nameError }`}>
                <input type="text" id='name' name='name' placeholder="이름" value={name} onChange={(e) => { setName(e.target.value); resetError(e); }} ref={ nameRef } />
                <div className="fieldError">
                  <p>{ nameErrorText }</p>
                </div>
              </div>
            </div>
            <div className="formRow fullWidth">
              <div className="fieldBox checkBox">
                <div className="eachBox">
                  <input type="checkbox" id='agree' name='agree' checked={ agree } onChange={(e) => { setAgree(!agree); resetError(e); } } />
                  <label htmlFor='agree'>
                    <button className={ styleClasses.underlineButton } onClick={ (event) => privacyPopup(event) }>개인정보 수집</button>에 동의합니다.
                  </label>
                </div>
              </div>
            </div>
            <div className="commonButton fullWidth">
              <button className="black" onClick={handleSignup}>
                회원가입
                <div className={`buttonLoading ${ buttonLoading && 'active' }`}>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </button>
            </div>
          </div>
          {error && <p>{error}</p>}
        </div>
      </div>
      <Popup open={ openPopup } setOpenPopup={ setOpenPopup } title={ popupTitle } text={ popupText } buttonText={ popupButtonText } buttonFunction={ popupFunction } />
    </>
  );
}


export default Signup;
